import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

export const searchUsers = async (search: string): Promise<any[]> => {
  if (search.length >= 3) {
    return axios.get(`graph-users/search?search=${search}`).then((res: any) => {
      if (res.status === 200) {
        const data = res.data;
        if (data.succeeded) {
          const list = data.data.map((d: any) => {
            return {
              key: d.id,
              label: d.displayName,
              value: d.id
            };
          });
          return list;
        } else {
          return [];
        }
      }
    });
  } else {
    return [];
  }
};

export interface AssignableUserOption {
  key: string;
  label: string;
  value: string;
}

export interface AssignableUsers {
  /**
   * True when `users` is every user the picker may offer, so it can be shown as a dropdown and
   * filtered locally. False on instances that are not restricted to app users, where the picker
   * has to keep searching the directory as the user types.
   */
  isComplete: boolean;
  users: AssignableUserOption[];
}

const NOT_LISTABLE: AssignableUsers = { isComplete: false, users: [] };

/** The list is small and changes only when somebody signs in for the first time. */
const ASSIGNABLE_USERS_TTL_MS = 5 * 60 * 1000;
let assignableUsersCache: { fetchedAt: number; value: AssignableUsers } | null = null;
let assignableUsersInFlight: Promise<AssignableUsers> | null = null;

/**
 * The users the Assigned To picker can list up front (FOC-5820). Cached briefly and shared between
 * concurrent callers so opening several pickers does not refetch it. Failures degrade to
 * search-as-you-type rather than blocking the picker.
 */
export const fetchAssignableUsers = async (now: number = Date.now()): Promise<AssignableUsers> => {
  if (assignableUsersCache && now - assignableUsersCache.fetchedAt < ASSIGNABLE_USERS_TTL_MS) {
    return assignableUsersCache.value;
  }
  if (assignableUsersInFlight) {
    return assignableUsersInFlight;
  }

  assignableUsersInFlight = axios
    .get("graph-users/assignable")
    .then((res: any) => {
      const data = res?.data;
      if (res?.status !== 200 || !data?.succeeded || !data?.data?.isComplete) {
        return NOT_LISTABLE;
      }
      const users = (data.data.users ?? []).map((d: any) => ({
        key: d.id,
        label: d.displayName,
        value: d.id
      }));
      return { isComplete: true, users };
    })
    .catch(() => NOT_LISTABLE)
    .then((value: AssignableUsers) => {
      assignableUsersCache = { fetchedAt: Date.now(), value };
      assignableUsersInFlight = null;
      return value;
    });

  return assignableUsersInFlight;
};

/**
 * The cached list, if one was loaded recently. Lets a picker render as a dropdown on its first
 * paint instead of flickering through the search-only fallback on every remount.
 */
export const getCachedAssignableUsers = (now: number = Date.now()): AssignableUsers | null => {
  if (assignableUsersCache && now - assignableUsersCache.fetchedAt < ASSIGNABLE_USERS_TTL_MS) {
    return assignableUsersCache.value;
  }
  return null;
};

/** Drops the cached list, so the next picker reloads it (used by tests and after sign-in). */
export const clearAssignableUsersCache = (): void => {
  assignableUsersCache = null;
  assignableUsersInFlight = null;
};

/**
 * Tells the API that the current user has signed in, so they become selectable in the Assigned To
 * picker when it is restricted to app users (FOC-5621). Best effort: failures are ignored.
 */
export const registerCurrentUser = (): Promise<void> => {
  return axios
    .post("graph-users/me")
    .then(() => {
      // The caller has just become assignable, so any list fetched before this is stale.
      clearAssignableUsersCache();
      return undefined;
    })
    .catch(() => undefined);
};

export const assignEmailToUser = async (emails: React.Key[], userId: string, userName: string): Promise<any[]> => {
  const body = {
    EmailGuids: emails,
    UserName: userName
  };
  return axios.post(`emails/${userId}/messagesassignuser`, body).then((res) => res.data);
};

/** Removes the Assigned To from the given documents (the detail page picker's clear "x"). */
export const unassignInvoiceUsers = async (invoiceIds: React.Key[]): Promise<any[]> => {
  return axios.post(`invoices/messagesunassignuser`, { AttachmentGuids: invoiceIds }).then((res) => res.data);
};

export const assignInvoiceToUser = async (emails: React.Key[], userId: string, userName: string): Promise<any[]> => {
  const body = {
    AttachmentGuids: emails,
    UserName: userName
  };
  return axios.post(`invoices/${userId}/messagesassignuser`, body).then((res) => res.data);
};

const initialUserState = {
  loading: false,
  userName: {} as string,
  roles: [] as string[],
};

const userSlice = createSlice({
  name: "user",
  initialState: initialUserState,
  reducers: {
    setUser(state, action: PayloadAction<string>) {
      state.userName = action.payload;
    },
    setRoles(state, action: PayloadAction<string[]>) {
      state.roles = action.payload;
    },
  },
  extraReducers: {
  },
});

export const { setUser, setRoles } = userSlice.actions;
export default userSlice.reducer;
